/**
 * /:wsId/p/:pKey/milestones — list of project milestones with progress and a
 * quick form to add more. Tasks can be assigned to a milestone from the
 * task drawer.
 */

import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import { ConfirmDialog } from '@/components/feedback'
import {
  useMilestones,
  useCreateMilestone,
  useUpdateMilestone,
  useArchiveMilestone,
  computeMilestoneProgress,
  formatDueDate,
} from '@/features/milestones/milestones'
import { useBoard } from '@/features/projects/board'
import { cn } from '@/lib/utils'
import type { Project, Milestone } from '@friday/shared/realtime'

export function MilestonesView() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  const navigate = useNavigate()

  const projectQ = useQuery({
    queryKey: ['project-by-key', wsId, pKey],
    queryFn: async () => {
      const list = await api<Project[]>(`/api/v1/workspaces/${wsId}/projects`)
      return list.find((p) => p.key === pKey) ?? null
    },
    enabled: !!wsId && !!pKey,
  })
  const project = projectQ.data

  const milestonesQ = useMilestones(wsId, project?.id)
  const board = useBoard(wsId, project?.id)
  const create = useCreateMilestone(wsId, project?.id)
  const update = useUpdateMilestone(wsId, project?.id)
  const archive = useArchiveMilestone(wsId, project?.id)
  const [pendingArchive, setPendingArchive] = useState<Milestone | null>(null)

  const [name, setName] = useState('')
  const [due, setDue] = useState('')
  const [color, setColor] = useState('#5B5BD6')

  if (projectQ.isLoading || !project) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-surface-500">
        Loading project…
      </div>
    )
  }

  if (milestonesQ.isLoading || board.isLoading) {
    return <div className="p-6 text-sm text-surface-500">Loading milestones…</div>
  }

  const milestones = milestonesQ.data ?? []
  const progressById = computeMilestoneProgress(board.data?.tasks ?? [])

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-surface-200 dark:border-surface-700 bg-white/60 dark:bg-surface-900/60 backdrop-blur px-4 md:pr-14 py-2">
        <button
          onClick={() => navigate({ to: '/w/$wsId/p/$pKey', params: { wsId, pKey } })}
          className="rounded p-1 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800"
          title="Back to board"
        >
          ←
        </button>
        <h1 className="text-sm font-semibold">
          Milestones · {project.key} · {project.name}
        </h1>
        <span className="text-[10px] text-surface-500">{milestones.length} total</span>
      </header>

      <div className="flex-1 overflow-auto p-4">
        <section className="card mb-4 p-4">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-surface-500">
            New milestone
          </h2>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!name.trim()) return
              try {
                await create.mutateAsync({
                  name: name.trim(),
                  dueDate: due ? new Date(due).toISOString() : null,
                  color,
                })
                setName('')
                setDue('')
                toast('Milestone created', { kind: 'success' })
              } catch (err) {
                const msg = err instanceof ApiError ? err.message : (err as Error).message
                toast(msg, { kind: 'error' })
              }
            }}
          >
            <label className="flex-1 min-w-[12rem]">
              <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Name</span>
              <input
                className="input mt-1"
                placeholder="e.g. Q1 launch"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label>
              <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Due date</span>
              <input
                type="date"
                className="input mt-1"
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </label>
            <label>
              <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Color</span>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1 h-9 w-12 cursor-pointer rounded border border-surface-300"
              />
            </label>
            <button type="submit" className="btn-primary" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Add milestone'}
            </button>
          </form>
        </section>

        {milestones.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-200 dark:border-surface-700 p-12 text-center text-sm text-surface-500">
            <div className="text-2xl">🎯</div>
            <p className="mt-2">No milestones yet. Group tasks into milestones to track what ships when.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {milestones.map((m) => (
              <MilestoneRow
                key={m.id}
                milestone={m}
                progress={progressById.get(m.id)}
                onPatch={(patch) => {
                  update.mutate(
                    { id: m.id, patch },
                    {
                      onError: (e) => {
                        const msg = e instanceof ApiError ? e.message : (e as Error).message
                        toast(msg, { kind: 'error' })
                      },
                    },
                  )
                }}
                onArchive={() => setPendingArchive(m)}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingArchive}
        title={`Archive "${pendingArchive?.name ?? ''}"?`}
        body={
          <p className="text-sm text-surface-600 dark:text-surface-300">
            The milestone will be hidden and any tasks assigned to it will be
            detached. Nothing is permanently deleted.
          </p>
        }
        confirmLabel="Archive milestone"
        destructive
        busy={archive.isPending}
        onClose={() => !archive.isPending && setPendingArchive(null)}
        onConfirm={async () => {
          if (!pendingArchive) return
          try {
            await archive.mutateAsync(pendingArchive.id)
            toast(`Milestone "${pendingArchive.name}" archived`, { kind: 'success' })
            setPendingArchive(null)
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Failed to archive milestone'
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </div>
  )
}

function MilestoneRow({
  milestone,
  progress,
  onPatch,
  onArchive,
}: {
  milestone: Milestone
  progress?: { total: number; done: number; ratio: number }
  onPatch: (patch: Partial<{ name: string; dueDate: string | null; color: string; status: Milestone['status'] }>) => void
  onArchive: () => void
}) {
  const total = progress?.total ?? 0
  const done = progress?.done ?? 0
  const ratio = progress?.ratio ?? 0
  return (
    <li className="card p-3">
      <div className="flex items-center gap-3">
        <ColorSwatch
          color={milestone.color}
          onChange={(c) => onPatch({ color: c })}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <InlineName
              value={milestone.name}
              onCommit={(v) => v.trim() && v.trim() !== milestone.name && onPatch({ name: v.trim() })}
            />
            <StatusSelect
              value={milestone.status}
              onChange={(s) => onPatch({ status: s })}
            />
          </div>
          <InlineDueDate
            value={milestone.dueDate}
            onCommit={(iso) => onPatch({ dueDate: iso })}
          />
          {total > 0 ? (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-surface-200 dark:bg-surface-700">
                <div
                  className="h-1.5 rounded-full bg-brand-500 transition-all"
                  style={{ width: `${Math.round(ratio * 100)}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-surface-500">
                {done}/{total}
              </span>
            </div>
          ) : (
            <div className="mt-1 text-[10px] text-surface-500">No tasks attached</div>
          )}
        </div>
        <button className="btn-ghost text-xs text-surface-500" onClick={onArchive}>
          Archive
        </button>
      </div>
    </li>
  )
}

function InlineName({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value)
  if (!editing) {
    return (
      <span
        className="truncate text-sm font-semibold cursor-text rounded px-1 -mx-1 hover:bg-surface-100 dark:hover:bg-surface-800"
        onClick={() => {
          setVal(value)
          setEditing(true)
        }}
        title="Click to rename"
      >
        {value}
      </span>
    )
  }
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        onCommit(val)
        setEditing(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setEditing(false)
      }}
      className="input h-6 py-0 text-sm font-semibold"
    />
  )
}

function StatusSelect({ value, onChange }: { value: Milestone['status']; onChange: (s: Milestone['status']) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Milestone['status'])}
      className={cn(
        'rounded-full border-0 px-1.5 py-0.5 text-[10px] font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-500',
        value === 'completed' && 'bg-green-500/10 text-green-700 dark:text-green-300',
        value === 'in_progress' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
        value === 'missed' && 'bg-red-500/10 text-red-700 dark:text-red-300',
        value === 'planned' && 'bg-surface-200 text-surface-600',
      )}
    >
      <option value="planned">planned</option>
      <option value="in_progress">in_progress</option>
      <option value="completed">completed</option>
      <option value="missed">missed</option>
    </select>
  )
}

function InlineDueDate({ value, onCommit }: { value: string | null; onCommit: (iso: string | null) => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value ? value.slice(0, 10) : '')
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setVal(value ? value.slice(0, 10) : '')
          setEditing(true)
        }}
        className="text-left text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
        title="Click to change"
      >
        {formatDueDate(value)}
      </button>
    )
  }
  return (
    <input
      autoFocus
      type="date"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        onCommit(val ? new Date(val).toISOString() : null)
        setEditing(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setEditing(false)
      }}
      className="input mt-1 h-6 py-0 text-xs"
    />
  )
}

function ColorSwatch({ color, onChange }: { color: string; onChange: (c: string) => void }) {
  return (
    <label
      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-white"
      style={{ backgroundColor: color }}
      title="Click to change color"
    >
      🎯
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
    </label>
  )
}
